import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { addEditingRequest, updateEditingRequestAdmin } from "services";

import { formatDate, useGetVerificationEditingRequests } from "utils";

import { ADD_EDITING_REQUEST, GET_FIELD_VERIFICATIONS, GET_VERIFICATION_EDITING_REQUESTS, UPDATE_EDITING_REQUEST } from "consts";

import { AddEditingRequestRequestBody, EDITING_REQUEST_TYPE, EditingRequestType, VerificationEditingRequest, editingRequestIdentifierMap } from "models";

import { Button, Flex, Form, IconLinkButton, Modal, Option, Select, SkeletonWrapper, TagNormal, TextArea, Typography, Message as message } from "components";

type RequestType = AddEditingRequestRequestBody["requestType"];
type CropType = AddEditingRequestRequestBody["cropType"];

// Practice types an editing request can target directly on an existing row (excludes the field-level types)
export type TargetableRequestType = Exclude<EditingRequestType, "fieldShape" | "fieldSplit" | "fieldRedzone">;

export type EditingRequestTarget = {
  requestType: TargetableRequestType;
  id: string;
  label: string;
} | null;

const FIELD_LEVEL_TYPES: EditingRequestType[] = [EDITING_REQUEST_TYPE.FIELD_SHAPE, EDITING_REQUEST_TYPE.FIELD_SPLIT, EDITING_REQUEST_TYPE.FIELD_REDZONE];

// True when a request has none of its practice/field identifiers set, i.e. it's a "missing practice" request
const hasNoIdentifier = (request: VerificationEditingRequest): boolean =>
  !request.fieldCropId && !request.fieldMineralFertilizerId && !request.fieldOrganicFertilizerId && !request.fieldTillageId && !request.fieldSeedingId;

/**
 * Formats the editing request type into a user-friendly display label
 */
const formatEditingRequestType = (requestType: EditingRequestType): string => {
  const typeLabels: Record<EditingRequestType, string> = {
    [EDITING_REQUEST_TYPE.CROP]: "Crop data",
    [EDITING_REQUEST_TYPE.FIELD_SHAPE]: "Field shape",
    [EDITING_REQUEST_TYPE.FIELD_SPLIT]: "Field split",
    [EDITING_REQUEST_TYPE.FIELD_REDZONE]: "Field redzone",
    [EDITING_REQUEST_TYPE.MINERAL_FERTILIZER]: "Mineral fertiliser",
    [EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER]: "Organic fertiliser",
    [EDITING_REQUEST_TYPE.TILLAGE]: "Tillage",
    [EDITING_REQUEST_TYPE.SEEDING]: "Seeding",
  };

  return typeLabels[requestType] || requestType;
};

export const EditingRequestModal = ({
  target,
  missingCropYear,
  reviewOnlyEditingRequests,
  verificationYear,
  fieldId,
  farmId,
  editingRequests = [],
  showModal,
  onClose,
}: {
  target?: EditingRequestTarget;
  missingCropYear?: number | null;
  // when set, the modal only lists these specific requests for review — no "create new request" flow, since
  // they don't correspond to a single existing row to target (e.g. a request whose practice was replaced)
  reviewOnlyEditingRequests?: VerificationEditingRequest[];
  verificationYear: number;
  fieldId: string;
  farmId: string;
  editingRequests?: VerificationEditingRequest[];
  showModal: boolean;
  onClose: () => void;
}) => {
  const isReviewOnly = !!reviewOnlyEditingRequests;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Helper function to get verifier name from the editing request data
  const getVerifierName = (request: VerificationEditingRequest) => request.verifier?.name || "Unknown Verifier";

  // Helper function to get CSS class name for request status
  const getRequestStatusClass = (request: VerificationEditingRequest) => {
    const status = getRequestStatus(request);

    // Map status to CSS class name
    const statusClassMap: Record<string, string> = {
      Pending: "pending",
      Completed: "completed",
      "Completed & Reviewed": "completed-and-reviewed",
    };

    const statusClass = statusClassMap[status] || "pending";
    return `editing-request-status ${statusClass}`;
  };

  const { isFetching: isFetchingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  const buildAddRequestBody = ({
    comment,
    requestType,
    cropType,
  }: {
    comment: string;
    requestType?: RequestType;
    cropType?: CropType;
  }): AddEditingRequestRequestBody => {
    const base = { fieldId, comment, verificationYear };

    if (target) {
      return {
        ...base,
        requestType: target.requestType,
        fieldCropId: target.requestType === EDITING_REQUEST_TYPE.CROP ? target.id : null,
        fieldMineralFertilizerId: target.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER ? target.id : null,
        fieldOrganicFertilizerId: target.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER ? target.id : null,
        fieldTillageId: target.requestType === EDITING_REQUEST_TYPE.TILLAGE ? target.id : null,
        fieldSeedingId: target.requestType === EDITING_REQUEST_TYPE.SEEDING ? target.id : null,
      };
    }

    if (missingCropYear) {
      return {
        ...base,
        requestType: EDITING_REQUEST_TYPE.CROP,
        year: missingCropYear,
        cropType: cropType ?? null,
      };
    }

    return { ...base, requestType: requestType! };
  };

  const { mutate: addMutate, isPending: addIsPending } = useMutation({
    mutationKey: [ADD_EDITING_REQUEST],
    mutationFn: (formValues: { comment: string; requestType?: RequestType; cropType?: CropType }) => addEditingRequest(buildAddRequestBody(formValues)),
    onSuccess: () => {
      message({ icon: "gremlin", content: "Editing request created successfully" });
      // Invalidate the editing requests query to refresh the data
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      // Also invalidate field verifications to update the editing request counts
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS] });

      // Reset the form but keep the modal open to show the new request
      form.resetFields();
    },
  });

  const { mutate: reviewEditingRequest, isPending: reviewIsPending } = useMutation({
    mutationKey: [UPDATE_EDITING_REQUEST],
    mutationFn: (request: VerificationEditingRequest) =>
      updateEditingRequestAdmin({
        id: request.id,
        reviewedAt: moment().toISOString(),
        // If not yet completed we also want to complete it with this review
        completedAt: !request.completedAt ? moment().toISOString() : undefined,
      }),
    onSuccess: () => {
      message({ icon: "gremlin", content: "Editing request resolved" });
      // Invalidate the editing requests query to refresh the data
      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_EDITING_REQUESTS, farmId] });
      // Also invalidate field verifications to update the editing request counts
      queryClient.invalidateQueries({ queryKey: [GET_FIELD_VERIFICATIONS] });
    },
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  // Filter editing requests based on context:
  // - reviewOnlyEditingRequests set: show exactly those (no row/year/field to target a new request against)
  // - target set: requests for that exact practice row
  // - missingCropYear set: "missing crop" requests for that year
  // - neither: field-level requests (fieldShape/fieldSplit/fieldRedzone)
  const relevantEditingRequests = isReviewOnly
    ? reviewOnlyEditingRequests
    : target
    ? editingRequests.filter((request) => request.requestType === target.requestType && request[editingRequestIdentifierMap[target.requestType]] === target.id)
    : missingCropYear
    ? editingRequests.filter((request) => request.year === missingCropYear && request.requestType === EDITING_REQUEST_TYPE.CROP && hasNoIdentifier(request))
    : editingRequests.filter((request) => FIELD_LEVEL_TYPES.includes(request.requestType));

  // Sort by creation date (newest first)
  const sortedEditingRequests = relevantEditingRequests.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf());

  const getRequestStatus = (request: VerificationEditingRequest) => {
    if (request.completedAt && request.reviewedAt) return "Completed & Reviewed";
    if (request.completedAt) return "Completed";
    return "Pending";
  };

  // Check if there are any pending or unconfirmed editing requests
  const hasPendingRequests = sortedEditingRequests.some((request) => !request.completedAt || (request.completedAt && !request.reviewedAt));

  // Custom footer with past editing requests
  const customFooter = (
    <div className="editing-request-modal-footer">
      {!isReviewOnly && (
        <div className="modal-buttons">
          <Button onClick={handleClose}>Close</Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={addIsPending}
            disabled={hasPendingRequests}
            title={hasPendingRequests ? "Cannot create new request while there are pending or unconfirmed requests" : ""}
          >
            Create request
          </Button>
        </div>
      )}

      {!isReviewOnly && hasPendingRequests && (
        <div className="editing-request-warning">
          <Typography variant="paragraph-xs" className="text-disabled">
            Cannot create new editing request while there are uncompleted or unreviewed requests.
          </Typography>
        </div>
      )}

      {/* Past Editing Requests Section */}
      {sortedEditingRequests.length > 0 && (
        <div className="editing-requests-history">
          <Typography variant="h4" className="margin-bottom-100">
            Past Editing Requests {target ? `for ${target.label}` : "for this field"}
          </Typography>
          <div className="editing-requests-list">
            {sortedEditingRequests.map((request) => (
              <div key={request.id} className="editing-request-item">
                <div className="margin-bottom-50">
                  <Typography variant="paragraph-xs" className="text-disabled">
                    {formatDate(request.createdAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}
                  </Typography>
                  <div className="paragraph-xs text-disabled editing-request-verifier">Created by: {getVerifierName(request)}</div>
                </div>

                <Flex justify="left" className="margin-bottom-100">
                  <SkeletonWrapper isLoading={reviewIsPending || isFetchingEditingRequests} type="button" buttonShape="round" buttonSize="small">
                    <TagNormal className="margin-right-50" text={formatEditingRequestType(request.requestType)} />
                    <TagNormal className={getRequestStatusClass(request)} text={getRequestStatus(request)} />
                  </SkeletonWrapper>

                  {getRequestStatus(request) !== "Completed & Reviewed" && (
                    <IconLinkButton
                      className="margin-left-50"
                      iconName="tabler/check"
                      text="Resolve"
                      disabled={reviewIsPending || isFetchingEditingRequests}
                      onClick={() => reviewEditingRequest(request)}
                    />
                  )}
                </Flex>

                {request.comment && <div className="paragraph-s margin-bottom-50">Request: {request.comment}</div>}
                {request.userComment && (
                  <div className="editing-request-user-comment">
                    <div className="paragraph-s margin-bottom-50">User Response: {request.userComment}</div>
                  </div>
                )}
                {(request.completedAt || request.reviewedAt) && (
                  <div className="editing-request-timeline">
                    {request.completedAt && (
                      <div className="paragraph-xs text-disabled">
                        Completed: {formatDate(request.completedAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}
                      </div>
                    )}
                    {request.reviewedAt && (
                      <div className="paragraph-xs text-disabled">
                        Reviewed: {formatDate(request.reviewedAt.toString(), "MMM DD, YYYY HH:mm", moment.ISO_8601)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      className="editing-request-modal"
      showModal={showModal}
      onOk={() => form.submit()}
      onCancel={handleClose}
      okText={"Create editing request"}
      cancelText={"Cancel"}
      title={"Editing request"}
      loading={addIsPending}
      footer={customFooter}
      content={
        !isReviewOnly && (
          <>
            <Typography variant="paragraph-m">Send a message to the farmer requesting corrections for {target?.label ?? "this field"}.</Typography>
            <Form form={form} onFinish={addMutate} className="padding-50">
              {/* Field-level request: we need to know what needs to be edited */}
              {!target && !missingCropYear && (
                <Select name="requestType" label="What needs to be edited?" className="margin-bottom-100" rules={[{ required: true }]}>
                  <Option value="fieldShape" key="fieldShape">
                    Field shape
                  </Option>
                  <Option value="fieldSplit" key="fieldSplit">
                    Field split
                  </Option>
                  <Option value="fieldRedzone" key="fieldRedzone">
                    Field redzone
                  </Option>
                </Select>
              )}

              {/* Missing crop request: which type of crop is missing */}
              {missingCropYear && (
                <Select name="cropType" label="Which crop is missing?" className="margin-bottom-100" rules={[{ required: true }]}>
                  <Option value="MAIN" key="main">
                    Main crop
                  </Option>
                  <Option value="COVER" key="cover">
                    Cover crop
                  </Option>
                  <Option value="UNDERSOWING" key="undersowing">
                    Undersowing
                  </Option>
                </Select>
              )}

              <TextArea name="comment" label="Leave a comment" />
            </Form>
          </>
        )
      }
    />
  );
};
